import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.card, borderColor: `${color}22` }}>
      <div style={{ ...S.cardIcon, background: `${color}18`, color }}>{icon}</div>
      <div style={S.cardBody}>
        <div style={S.cardValue}>{value ?? '—'}</div>
        <div style={S.cardLabel}>{label}</div>
        {sub && <div style={S.cardSub}>{sub}</div>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats/').then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={S.root}>
        <div style={S.center}>
          <div style={S.spinner} />
        </div>
      </div>
    );
  }

  if (!stats) return <div style={S.root}><div style={S.center}><p style={{ color: '#64748b' }}>Failed to load stats</p></div></div>;

  const { users, messages, random_chat, posts } = stats;

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Dashboard</h1>
          <p style={S.subtitle}>Platform overview & statistics</p>
        </div>
        <div style={S.badge}>Live</div>
      </div>

      <div style={S.grid}>
        <StatCard icon="👥" label="Total Users" value={users.total} sub={`${users.online} online · ${users.new_this_week} new this week`} color="#6366f1" />
        <StatCard icon="🧑‍💻" label="Staff Members" value={users.staff} sub={`${users.guests} guest accounts`} color="#8b5cf6" />
        <StatCard icon="💬" label="Messages" value={messages.total} sub={`+${messages.new_this_week} this week · ${messages.with_files} with files`} color="#06b6d4" />
        <StatCard icon="🎲" label="Random Rooms" value={random_chat.total_rooms} sub={`${random_chat.active_rooms} active · ${messages.random} messages`} color="#f59e0b" />
        <StatCard icon="📸" label="Posts" value={posts.total} sub={`${posts.comments} comments · ${posts.new_this_week} new`} color="#10b981" />
        <StatCard icon="👤" label="Online Now" value={users.online} sub="Currently active users" color="#f43f5e" />
      </div>

      {/* Quick Overview */}
      <div style={S.overviewGrid}>
        <div style={S.overviewCard}>
          <div style={S.overviewTitle}>📊 This Week's Activity</div>
          <div style={S.overviewList}>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>New users</span>
              <span style={{ color: '#6366f1', fontWeight: 700 }}>+{users.new_this_week}</span>
            </div>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>New messages</span>
              <span style={{ color: '#06b6d4', fontWeight: 700 }}>+{messages.new_this_week}</span>
            </div>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>New posts</span>
              <span style={{ color: '#10b981', fontWeight: 700 }}>+{posts.new_this_week}</span>
            </div>
          </div>
        </div>

        <div style={S.overviewCard}>
          <div style={S.overviewTitle}>📈 Platform Health</div>
          <div style={S.overviewList}>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>Engagement ratio</span>
              <span style={{ color: '#10b981', fontWeight: 700 }}>
                {users.total > 0 ? Math.round((users.online / users.total) * 100) : 0}% online
              </span>
            </div>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>Random chat activity</span>
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>{random_chat.active_rooms} rooms</span>
            </div>
            <div style={S.overviewRow}>
              <span style={{ color: '#94a3b8' }}>Comments per post</span>
              <span style={{ color: '#8b5cf6', fontWeight: 700 }}>
                {posts.total > 0 ? (posts.comments / posts.total).toFixed(1) : 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', padding: 28, background: '#07070d', animation: 'fadeIn 0.3s ease' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 },
  spinner: { width: 32, height: 32, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title: { margin: 0, fontSize: 24, fontWeight: 800, color: '#e2e8f0', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', color: '#475569', fontSize: 12 },
  badge: {
    padding: '5px 12px', borderRadius: 20, background: 'rgba(16,185,129,0.15)',
    color: '#34d399', fontSize: 10, fontWeight: 700, border: '1px solid rgba(16,185,129,0.2)',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 24 },
  card: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid',
    borderRadius: 16, padding: '20px 20px', display: 'flex', gap: 16, alignItems: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
    animation: 'fadeIn 0.4s ease',
  },
  cardIcon: { width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },
  cardBody: {},
  cardValue: { fontSize: 26, fontWeight: 800, color: '#e2e8f0', lineHeight: 1 },
  cardLabel: { fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 },
  cardSub: { fontSize: 10, color: '#475569', marginTop: 4 },
  overviewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  overviewCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 20 },
  overviewTitle: { fontWeight: 700, color: '#e2e8f0', fontSize: 12, marginBottom: 16 },
  overviewList: { display: 'flex', flexDirection: 'column', gap: 12 },
  overviewRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 },
};
